import React, { useEffect, useState, useCallback } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Button } from "@mui/material";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import TableLoadingShell from "src/utils/TableLoadingShell";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/redux/store";
import { deleteWarehouseDataAsync, getWarehouseListAsync } from "src/redux/actions/omsPageActions";
import { KTIcon } from "src/marketConnect/helpers";
import CreateEditWarehouseModal from "./CreateUpdateWarehouse";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import { toast } from "react-toastify";
import { formatDateLocal, formatTimeZone } from "src/utils/TimeZoneDateFormat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faStar } from "@fortawesome/free-solid-svg-icons";


const WarehouseListing: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchString, setSearchString] = useState("");
    const [paginationData, setPaginationData] = useState({
        page_num: 1,
        page_limit: 10,
        search: "",
        sort_by: "id",
        sort_order: "asc",
        warehouse_id: null,
        isActive: true,
    });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);

    // Redux Selectors
    const { loading, warehouseList, pagination } = useSelector(
        (state: any) => state.warehouseSlice
    );

    // fetch API
    const fetchWarehouse = useCallback(() => {
        dispatch(
            getWarehouseListAsync({
                paramData: {
                    page: paginationData.page_num,
                    limit: paginationData.page_limit,
                    search: paginationData.search,
                    sort_by: paginationData.sort_by,
                    sort_order: paginationData.sort_order,
                    warehouse_id: paginationData.warehouse_id,
                    include: "address,additional_data"
                },
            })
        ).unwrap()
            .catch((error) => {
                console.log(error, "warehouse err");
                toast.error(error?.data?.message || "Something went wrong while fetching");
            });
    }, [paginationData]);

    useEffect(() => {
        fetchWarehouse();
    }, [fetchWarehouse]);

    // Create button
    const handleCreate = () => {
        setEditData(null);        // Clear edit mode
        setShowCreateModal(true);
    };

    // Action Handlers
    const handleEdit = (row: any) => {
        setEditData(row);
        setShowCreateModal(true);
    };

    const handleDelete = (warehouse: any) => {
        setSelectedWarehouse(warehouse);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        if (!selectedWarehouse) return;

        dispatch(deleteWarehouseDataAsync({ id: selectedWarehouse.id }))
            .then((res: any) => {
                const response = res.payload;
                toast.success(response.message || "Warehouse deleted successfully!");
                fetchWarehouse(); // refresh the list
            })
            .catch((err: any) => {
                toast.error(err?.message || "Failed to delete warehouse");
            })
            .finally(() => {
                setShowDeleteModal(false);
                setSelectedWarehouse(null);
            });
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setSelectedWarehouse(null);
    };

    const columns: TableColumn<any>[] = [
        {
            name: "Name",
            selector: (row) => row.api_name || "-",
            sortable: true,
            minWidth: "180px",
            cell: (row) => (
                <span className="d-inline-flex align-items-center gap-2">
                    {row.is_default === true ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Default warehouse</Tooltip>}
                        >
                            <span style={{ cursor: "pointer" }}>
                                <FontAwesomeIcon
                                    icon={faStar}
                                    className="text-warning"
                                    style={{ fontSize: "12px" }}
                                />
                            </span>
                        </OverlayTrigger>
                    ) : null}
                    <span>{row.api_name || "-"}</span>
                </span>
            ),
        },
        {
            name: "Status",
            minWidth: "120px",
            cell: (row) => (
                <span
                    className={`badge text-capitalize px-3 py-2 rounded-pill bg-${row.isActive ? "success" : "secondary"}`}
                >
                    {row.isActive ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Created At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            minWidth: "160px",
            center: true,
            cell: (row) => (
                row?.created_at ? formatDateLocal(row.created_at) : ""),
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Updated At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            minWidth: "160px",
            center: true,
            cell: (row) => (
                row?.updated_at ? formatDateLocal(row.updated_at) : ""),
        },
        {
            name: "Action",
            cell: (row) => (
                <div className="abd-actions d-flex align-items-center gap-2">
                    <button
                        className="btn btn-icon btn-bg-light btn-color-primary btn-sm me-3 mt-1"
                        onClick={() => handleEdit(row)} >
                        <KTIcon iconName="pencil" className="fs-4" />
                    </button>

                    <button
                        className="btn btn-icon btn-bg-light btn-color-danger btn-sm mt-1"
                        onClick={() => handleDelete(row)} >
                        <KTIcon iconName="trash" className="fs-4" />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            maxWidth: "140px",
        },
    ];

    return (
        <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold text-lg" style={{ fontSize: "16px" }}>
                    Warehouse Details
                </span>
            </div>
            <div className="card card-flush">
                <div className="card-header d-flex justify-content-between align-items-center">
                    {/* SEARCH BOX - LEFT */}
                    <div className="position-relative">
                        {searchString === "" && (
                            <i className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"></i>
                        )}
                        <input
                            className="form-control mr-sm-2 input-sm "
                            type="search"
                            placeholder="Search"
                            aria-label="Search"
                            onChange={(ev) => setSearchString(ev.target.value)}
                            onKeyDown={(ev) => {
                                if (ev.key === "Enter") {
                                    setPaginationData((prev) => ({
                                        ...prev,
                                        search: searchString,
                                        page_num: 1,
                                    }));
                                }
                            }}
                            value={searchString}
                        />
                    </div>

                    {/* CREATE BUTTON - RIGHT */}
                    {/* <Button variant="contained" className="btn btn-sm btn-primary w-180px fw-bold py-2" onClick={handleCreate}>
                        + Add Warehouse
                    </Button> */}
                    <button className="btn btn-sm btn-primary fw-bold py-2"
                        onClick={handleCreate}
                    >+ Create New Warehouse
                    </button>
                </div>

                <TableLoadingShell loading={loading}>
                <DataTable
                    fixedHeader
                    data={warehouseList}
                    columns={columns}
                    persistTableHead
                    pagination
                    paginationServer
                    paginationTotalRows={pagination.count}
                    paginationPerPage={paginationData.page_limit}
                    onChangePage={(page) =>
                        setPaginationData((prev) => ({ ...prev, page_num: page }))
                    }
                    onChangeRowsPerPage={(newLimit) =>
                        setPaginationData((prev) => ({
                            ...prev,
                            page_limit: newLimit,
                            page_num: 1,
                        }))
                    }
                    progressPending={false}
                    noDataComponent={
                        <div className="text-secondary">
                            <h5>No records found</h5>
                        </div>
                    }
                    customStyles={{
                        table: { style: { height: "55vh" } },
                        headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
                        cells: {
                            style: {
                                padding: "10px",
                                borderBottom: "1px solid gainsboro",
                            },
                        },
                    }}
                />
                </TableLoadingShell>
            </div>
            <CreateEditWarehouseModal
                show={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    setEditData(null); // clear edit state
                }}
                editData={editData}
                onSaved={() => {
                    fetchWarehouse();     // refresh only on save
                    setShowCreateModal(false);
                    setEditData(null);
                }}
            />

            <CommonAlertModal
                title="Are you sure to delete warehouse detail?"
                subtitle="Particular warehouse data will delete "
                isVisible={showDeleteModal}
                isTermsVisible={false}
                onSuccess={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                successBtnTitle="Yes, Delete"
                cancelBtnTitle="Cancel"
                successBtnClasses="btn btn-sm btn-danger m-2"
            />
        </>
    );

};

export default WarehouseListing;
